import React, { useState, useEffect } from 'react';
import { Search, MessageSquare, Users } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { chatService } from '../services/chatService';
import { authService } from '../services/authService';

const ChatList = ({ onSelectConversation, selectedConversationId, typeFilter = 'all' }) => {
  const { currentUser, userData } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [participantNames, setParticipantNames] = useState({});
  const [search, setSearch] = useState('');
  const [lastSelectedId, setLastSelectedId] = useState(null);
  const [localReadStatus, setLocalReadStatus] = useState({});

  useEffect(() => {
    if (!currentUser) return;
    setLoading(true);
    const unsubscribe = chatService.subscribeToConversations(
      currentUser.uid,
      (conversationsData) => {
        setConversations(conversationsData || []);
        setLoading(false);
        
        // Sync local read status with Firestore data to prevent stale local state
        if (conversationsData) {
          setLocalReadStatus(prev => {
            const updated = { ...prev };
            conversationsData.forEach(conv => {
              const lastReadByUser = conv.lastReadBy?.[currentUser.uid];
              if (lastReadByUser) {
                // If Firestore shows it's read, remove from local state
                delete updated[conv.id];
              }
            });
            return updated;
          });
        }
      }
    );
    return unsubscribe;
  }, [currentUser]);

  // Reset local read status when user changes
  useEffect(() => {
    setLocalReadStatus({});
  }, [currentUser?.uid]);

  useEffect(() => {
    // Fetch participant names for all conversations
    const fetchParticipantNames = async () => {
      const names = {};
      const uniqueUserIds = new Set();
      
      conversations.forEach(conv => {
        conv.participants.forEach(participantId => {
          if (participantId !== currentUser.uid) {
            uniqueUserIds.add(participantId);
          }
        });
      });

      for (const userId of uniqueUserIds) {
        try {
          const userData = await authService.getUserData(userId);
          names[userId] = userData?.displayName || userData?.email || 'Unknown User';
        } catch (error) {
          console.error('Error fetching user data:', error);
          names[userId] = 'Unknown User';
        }
      }

      setParticipantNames(names);
    };

    if (conversations.length > 0) {
      fetchParticipantNames();
    }
  }, [conversations, currentUser.uid]);

  // Handle conversation selection with debouncing
  const handleConversationClick = (conversation) => {
    // Prevent rapid clicking on the same conversation
    if (lastSelectedId === conversation.id) {
      return;
    }
    
    setLastSelectedId(conversation.id);
    
    // Immediately mark conversation as read locally to prevent notification bubble flicker
    if (conversation.lastMessageTime && conversation.lastMessageSenderId !== currentUser.uid) {
      setLocalReadStatus(prev => ({
        ...prev,
        [conversation.id]: new Date()
      }));
    }
    
    onSelectConversation(conversation);
    
    // Reset after a short delay to allow for legitimate re-selection
    setTimeout(() => setLastSelectedId(null), 300);
  };

  const formatLastMessageTime = (timestamp) => {
    if (!timestamp) return '';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 48) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  };

  const getConversationTitle = (conversation) => {
    if (conversation.title) {
      return conversation.title;
    }

    if (conversation.type === 'direct') {
      const otherParticipantId = conversation.participants.find(id => id !== currentUser.uid);
      return participantNames[otherParticipantId] || 'Direct Message';
    }

    return 'Group Chat';
  };

  const getInitials = (name) => {
    if (!name) return '??';
    const parts = name.trim().split(/\s+/);
    return parts.slice(0, 2).map(p => p[0]?.toUpperCase() || '').join('');
  };

  // Only show conversations that have at least one message
  const nonEmptyConversations = conversations.filter(
    (c) => Boolean(c.lastMessageTime) || Boolean(c.lastMessage)
  );

  const filteredByType = nonEmptyConversations.filter((conversation) => {
    if (typeFilter === 'all') return true;
    return conversation.type === typeFilter;
  });

  const filteredConversations = filteredByType.filter((conversation) => {
    const title = getConversationTitle(conversation).toLowerCase();
    const lastMessage = (conversation.lastMessage || '').toLowerCase();
    const propertyName = (conversation.propertyName || '').toLowerCase();
    const query = search.trim().toLowerCase();
    if (!query) return true;
    return (
      title.includes(query) ||
      lastMessage.includes(query) ||
      propertyName.includes(query)
    );
  });

  const getConversationSubtitle = (conversation) => {
    if (conversation.type === 'direct') {
      return conversation.propertyName || '';
    }
    
    if (conversation.type === 'group') {
      return conversation.ticketTitle || 'Group Chat';
    }

    return '';
  };

  const getUnreadCount = (conversation) => {
    // Do not show unread for the sender themselves when they just sent the last message
    if (conversation.lastMessageSenderId === currentUser.uid) return 0;
    
    // Check local read status first (for immediate UI updates)
    const localReadTime = localReadStatus[conversation.id];
    if (localReadTime) {
      return 0; // Already marked as read locally
    }
    
    const lastReadByUser = conversation.lastReadBy?.[currentUser.uid];
    const lastMessageTime = conversation.lastMessageTime;
    if (!lastMessageTime) return 0;
    const lastMessageDate = lastMessageTime.toDate ? lastMessageTime.toDate() : new Date(lastMessageTime);
    const lastReadDate = lastReadByUser?.toDate ? lastReadByUser.toDate() : (lastReadByUser ? new Date(lastReadByUser) : null);
    return lastReadDate && lastReadDate >= lastMessageDate ? 0 : 1;
  };

  const getConversationIcon = (conversation) => {
    if (conversation.type === 'direct') {
      return (
        <div className="w-10 h-10 bg-slate-200 rounded-lg flex items-center justify-center text-slate-700 font-medium text-sm uppercase">
          {getInitials(participantNames[conversation.participants.find(id => id !== currentUser.uid)])}
        </div>
      );
    }
    return (
      <div className="w-10 h-10 bg-slate-200 rounded-lg flex items-center justify-center text-slate-600">
        <Users className="w-5 h-5" strokeWidth={1.75} />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="w-80 bg-white border-r border-slate-200 flex flex-col">
        <div className="p-4 border-b border-slate-200">
          <div className="animate-pulse">
            <div className="h-5 bg-slate-200 rounded w-28 mb-3" />
            <div className="h-9 bg-slate-200 rounded-md w-full" />
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-2">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 border-t-slate-600" />
            <p className="text-sm text-slate-500">Loading conversations...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-80 bg-white border-r border-slate-200 flex flex-col">
      <div className="p-4 border-b border-slate-200 bg-slate-50/50">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-slate-800">Conversations</h2>
          <span className="text-xs text-slate-500">{filteredConversations.length} active</span>
        </div>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="w-4 h-4 text-slate-400" strokeWidth={1.75} />
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search conversations..."
            className="w-full pl-9 pr-3 py-2.5 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-300 bg-white placeholder-slate-400"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {filteredConversations.length === 0 ? (
          <div className="p-6 text-center">
            <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mx-auto mb-3">
              <MessageSquare className="w-6 h-6 text-slate-400" strokeWidth={1.5} />
            </div>
            <h3 className="text-sm font-medium text-slate-800 mb-1">No conversations yet</h3>
            <p className="text-xs text-slate-500">Start a conversation to begin messaging</p>
          </div>
        ) : (
          <div className="p-2">
            {filteredConversations.map((conversation) => {
              const unreadCount = getUnreadCount(conversation);
              const isSelected = selectedConversationId === conversation.id;
              return (
                <div
                  key={conversation.id}
                  onClick={() => handleConversationClick(conversation)}
                  className={`p-3 cursor-pointer rounded-md transition-colors mb-1 ${
                    isSelected ? 'bg-slate-100 border border-slate-200' : 'hover:bg-slate-50 border border-transparent'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">{getConversationIcon(conversation)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-0.5">
                        <h3 className={`text-sm font-medium truncate ${unreadCount > 0 ? 'text-slate-900' : 'text-slate-700'}`}>
                          {getConversationTitle(conversation)}
                        </h3>
                        {conversation.lastMessageTime && (
                          <span className="text-xs text-slate-400 flex-shrink-0">
                            {formatLastMessageTime(conversation.lastMessageTime)}
                          </span>
                        )}
                      </div>
                      {conversation.propertyName && (
                        <p className="text-xs text-slate-500 truncate mb-0.5">{conversation.propertyName}</p>
                      )}
                      {conversation.lastMessage && (
                        <p className={`text-xs truncate ${unreadCount > 0 ? 'text-slate-800 font-medium' : 'text-slate-600'}`}>
                          {conversation.lastMessageSenderId === currentUser.uid && 'You: '}
                          {conversation.lastMessage}
                        </p>
                      )}
                    </div>
                    {unreadCount > 0 && (
                      <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 text-[10px] font-semibold text-white bg-slate-600 rounded-full flex-shrink-0">
                        {unreadCount}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatList;
